import { useUser } from "@descope/react-sdk";
import { useEffect, useState } from "react";
import { SessionHandler } from "verdi-web-client";

type FeatureFlag = {
    global: boolean; // enable globally
    allAdmins: boolean; // enable for all admins
    // enable for users with the given user ids
    userIds?: string[];
    // enable for users with the given login ids
    loginIds?: string[];
    // enable for users who have access to the given organization ids
    organizationIds?: string[];
    // enable for admins with the given admin login ids
    adminLoginIds?: string[];
    // enable when hostname matches the given strings
    hostname: string[];
};

export enum FEATURE_FLAGS {
    ON_DEMAND_IRRIGATION = "ON_DEMAND_IRRIGATION",
    DEVICE_CARD_NETWORK_DETAILS = "DEVICE_CARD_NETWORK_DETAILS",
    DATA_VIEWER = "DATA_VIEWER",
    MULTIPLE_NOTIFICATION_TRIGGERS = "MULTIPLE_NOTIFICATION_TRIGGERS",
    NOTIFICATION_SUBSCRIPTIONS = "NOTIFICATION_SUBSCRIPTIONS",
}

/**
 * Feature flag configuration
 */
const featureFlagConfigurations: Record<FEATURE_FLAGS, FeatureFlag> = {
    [FEATURE_FLAGS.ON_DEMAND_IRRIGATION]: {
        global: false,
        allAdmins: false,
        adminLoginIds: ["michael.kimsing@verdiag.com", "michael.quan@verdiag.com"],
        organizationIds: [
            "608b4eb9cb713f0004608602", // fake account
            "682e82ac57f20544170f6519", // Goodfarms - Oxnard
            "6893816617fb4d000713f989", // S&B_Dhaliwal
            "69c3ee3cb34c6d3eb5506604", // Karstetter Enterprises LLC
            "5eb659a7fd1e82a9df674eee", // Arterra
            "6a072cd6e71361debd518278", // Helen's Acres
        ],
        hostname: ["app.test-0.verdiag.com"],
    },
    [FEATURE_FLAGS.DEVICE_CARD_NETWORK_DETAILS]: {
        global: false,
        allAdmins: true,
        loginIds: [
            "ygudz@vandenbussche.com",
            "gvandenbussche@vandenbussche.com",
            "ssmith@vandenbussche.com",
            "rmackenzie@vandenbussche.com",
        ],
        hostname: [],
    },
    [FEATURE_FLAGS.DATA_VIEWER]: {
        global: false,
        allAdmins: false,
        adminLoginIds: ["michael.quan@verdiag.com"],
        hostname: [],
    },
    // Multiple triggers per notification subscription. Off for everyone in every environment for now;
    // with it off a single-trigger subscription renders its scopes and warning types without card chrome.
    [FEATURE_FLAGS.MULTIPLE_NOTIFICATION_TRIGGERS]: {
        global: false,
        allAdmins: false,
        hostname: [],
    },
    // Rollout gate for the SMS notification subscriptions settings card. Set `global` to release it to
    // every user.
    [FEATURE_FLAGS.NOTIFICATION_SUBSCRIPTIONS]: {
        global: false,
        allAdmins: true,
        hostname: [],
    },
};

/**
 * Check if a feature flag is enabled for the given login id and role names
 */
export const isFeatureFlagEnabled = (
    feature: FEATURE_FLAGS,
    currentUserId?: string,
    loginIds?: string[],
    currentOrganizationId?: string,
) => {
    const featureFlag = featureFlagConfigurations[feature];

    if (!featureFlag) {
        return false;
    }

    // Feature is enabled globally
    if (featureFlag.global) {
        return true;
    }

    // Feature is enabled for all admins
    if (featureFlag.allAdmins && SessionHandler.admin) {
        return true;
    }

    // Feature is enabled for admin users with the given login ids
    if (featureFlag.adminLoginIds?.length && SessionHandler.admin) {
        // Admin but not in adminLoginIds, fall through to check userIds
        if (featureFlag.adminLoginIds.some((adminLoginId) => loginIds?.includes(adminLoginId))) {
            return true;
        }
    }

    // Feature is enabled for users with the given login ids
    if (featureFlag.loginIds?.length) {
        if (featureFlag.loginIds.some((loginId) => loginIds?.includes(loginId))) {
            return true;
        }
    }

    // Feature is enabled for users with the given user ids
    if (featureFlag.userIds?.length && currentUserId) {
        if (featureFlag.userIds.some((userId) => userId === currentUserId)) {
            return true;
        }
    }

    // Feature is enabled for a given organization id. The feature is enabled only when viewing the given organization
    if (featureFlag.organizationIds?.length && currentOrganizationId) {
        if (featureFlag.organizationIds.some((organizationId) => organizationId === currentOrganizationId)) {
            return true;
        }
    }

    // Feature is enabled when hostname matches the given strings
    if (featureFlag.hostname?.length) {
        if (featureFlag.hostname.some((hostname) => window.location.hostname === hostname)) {
            return true;
        }
    }

    return false;
};

/**
 * Hook to check if a feature flag is enabled for the current user
 */
export const useFeatureFlag = (feature: FEATURE_FLAGS) => {
    const currentUserId = SessionHandler.curUser;
    const { user } = useUser();
    const [currentOrganizationId, setCurrentOrganizationId] = useState(SessionHandler.currentUserObject?.id);

    // Update the current organization id when the session loads (ie. org changes)
    useEffect(() => {
        const handleSessionLoad = () => {
            setCurrentOrganizationId(SessionHandler.currentUserObject?.id);
        };
        SessionHandler.onSessionLoad.addListener(handleSessionLoad);
        return () => {
            SessionHandler.onSessionLoad.removeListener(handleSessionLoad);
        };
    }, []);

    const [isEnabled, setIsEnabled] = useState(
        isFeatureFlagEnabled(feature, currentUserId, user?.loginIds ?? [], currentOrganizationId),
    );

    useEffect(() => {
        setIsEnabled(isFeatureFlagEnabled(feature, currentUserId, user?.loginIds ?? [], currentOrganizationId));
    }, [feature, currentUserId, user?.loginIds, currentOrganizationId]);

    return { isEnabled };
};
